import { Product } from "@/lib/types";
import { CATEGORY_DISPLAY_NAMES } from "@/lib/categories";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight } from "lucide-react";

interface ProductCardProps {
  product: Product;
  onClick: (product: Product) => void;
  priceDisplay: string;
}

export function ProductCard({ product, onClick, priceDisplay }: ProductCardProps) {
  return (
    <Card
      className="overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer group"
      onClick={() => onClick(product)}
    >
      <div className="aspect-square w-full overflow-hidden bg-gradient-to-br from-primary/5 to-primary/10">
        {product.thumbnail ? (
          <img
            src={product.thumbnail}
            alt={product.name}
            width={400}
            height={400}
            loading="lazy"
            className="h-full w-full object-cover transition-transform group-hover:scale-105 duration-300"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <span className="text-sm text-muted-foreground">No image</span>
          </div>
        )}
      </div>

      <CardHeader className="pb-3">
        {product.categories && product.categories.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {product.categories.slice(0, 2).map(category => (
              <Badge key={category} variant="secondary" className="text-xs">
                {(CATEGORY_DISPLAY_NAMES as Record<string, string>)[category] || category}
              </Badge>
            ))}
          </div>
        )}
        <CardTitle className="line-clamp-1 text-lg">{product.name}</CardTitle>
        <CardDescription className="line-clamp-2 text-sm">{product.description}</CardDescription>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex items-center justify-between">
          <span className="text-xl font-bold text-accent">{priceDisplay}</span>
          <ArrowRight className="h-5 w-5 text-primary group-hover:translate-x-1 transition-transform" />
        </div>
      </CardContent>
    </Card>
  );
}
